import { Button } from '@helvetia/components/Button'
import Input from '@helvetia/components/forms/Input'
import { OptionSelectorField } from '@helvetia/components/forms/OptionSelector'
import { Col, Row } from '@helvetia/components/layout'
import LinkText from '@helvetia/components/LinkText'
import { MatchMedia } from '@helvetia/components/MatchMedia'
import Spinner from '@helvetia/components/Spinner'
import Text from '@helvetia/components/Text'
import { Field, Form, Formik } from 'formik'
import * as React from 'react'
import { WithNamespaces, withNamespaces } from 'react-i18next'
import { RouteComponentProps, withRouter } from 'react-router-dom'

import AgentService from '../../../core/services/agent.service'
import { CheckboxField } from '../../_components/CheckboxField'
import { QuestionWrapper } from '../../_components/common.styles'
import { LifecycleView } from '../../_components/ConnectComponent'
import { DropDownSelect } from '../../_components/DropDownSelect'
import ContactBox from '../../_components/HelvetiaContactBox'
import { goToPrivacyPolicyLink } from '../../_utils/links'
import { ContractStepper } from '../ContractStepper'
import { PolicyholderDataPresenter } from './PolicyholderData.presenter'
import { validateFieldForm } from './PolicyholderData.validators'

export interface View extends LifecycleView {
  showLoading: () => void
  hideLoading: () => void
}

export interface PolicyholderDataInfoValues {
  company: string
  firstName: string
  lastName: string
  email: string
  country: string
  postalAddress: string
  postalAddressOpt?: string
  postalCode: string
  city: string
  acceptedPrivacyPolicy: boolean
}

interface Props {
  presenter: PolicyholderDataPresenter
  onSubmit: (values: PolicyholderDataInfoValues) => void
}

interface State {
  initialValues?: PolicyholderDataInfoValues
  isLoading: boolean
  acceptedPrivacyPolicy: boolean
}

export class PolicyholderDataSection
  extends React.Component<Props & RouteComponentProps & WithNamespaces, State>
  implements View {
  private presenter: PolicyholderDataPresenter
  private countryOptions

  constructor(props) {
    super(props)

    this.presenter = props.presenter
    this.presenter.initialize(this)

    this.state = {
      initialValues: null,
      isLoading: false,
      acceptedPrivacyPolicy: false,
    }
  }

  setInitialValues = initialValues => {
    this.setState({
      initialValues,
      acceptedPrivacyPolicy: initialValues.acceptedPrivacyPolicy,
    })
  }

  showLoading = () => this.setState({ isLoading: true })

  hideLoading = () => this.setState({ isLoading: false })

  handleClickBack = () => this.presenter.onBackClick()

  handleClickContinuar = (values: PolicyholderDataInfoValues) =>
    this.presenter.onContinue(values)

  render() {
    const { t } = this.props
    if (!this.state.initialValues) return <Spinner size="xl" />
    this.countryOptions = [
      {
        text: t('contactData.country.switzerland'),
        value: 'switzerland',
      },
      { text: 'Liechtenstein', value: 'Liechtenstein' },
    ]
    return (
      <>
        <Row>
          <Col tablet={12} desktop={4} verticalGutter>
            <ContractStepper step={2} />
          </Col>

          <Col tablet={12} desktop={8}>
            <Row marginTop="xxs">
              <Text as="p" color="black" size="l">
                {t('contractStep2.title')}
              </Text>
            </Row>

            <Formik
              initialValues={this.state.initialValues}
              onSubmit={this.handleClickContinuar}
              validate={values => validateFieldForm(values)}
              render={({ setFieldValue, values }) => (
                <Form>
                  <Row marginTop="xs" marginBottom="s">
                    <Col>
                      <Field
                        component={Input}
                        name="company"
                        text={
                          AgentService.isAgentOrBroker()
                            ? t('contactData.company.agentQuestion')
                            : t('contactData.company.question')
                        }
                        label={t('contactData.company.label')}
                        required
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col mobile={12} tablet={6} desktop={6}>
                      <Row marginBottom="s">
                        <Col>
                          <Field
                            component={Input}
                            name="firstName"
                            text={
                              AgentService.isAgentOrBroker()
                                ? t('contactData.firstName.agentQuestion')
                                : t('contactData.firstName.question')
                            }
                            label={t('contactData.firstName.label')}
                            required
                          />
                        </Col>
                      </Row>
                    </Col>
                    <Col mobile={12} tablet={6} desktop={6}>
                      <Row marginBottom="s">
                        <Col>
                          <Field
                            component={Input}
                            name="lastName"
                            text={
                              AgentService.isAgentOrBroker()
                                ? t('contactData.lastName.agentQuestion')
                                : t('contactData.lastName.question')
                            }
                            label={t('contactData.lastName.label')}
                            required
                          />
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                  <Row>
                    <Col mobile={12} tablet={6} desktop={6}>
                      <Row marginBottom="s">
                        <Col>
                          <Field
                            component={Input}
                            name="email"
                            text={
                              AgentService.isAgentOrBroker()
                                ? t('contactData.email.agentQuestion')
                                : t('contactData.email.question')
                            }
                            label={t('contactData.email.label')}
                            required
                          />
                        </Col>
                      </Row>
                    </Col>
                    <Col mobile={12} tablet={6} desktop={6}>
                      <Row marginBottom="s">
                        <QuestionWrapper as="div" color="black">
                          {AgentService.isAgentOrBroker()
                            ? t('contactData.country.agentQuestion')
                            : t('contactData.country.question')}
                        </QuestionWrapper>
                        <Col>
                          <MatchMedia onlyMobile>
                            <Field
                              component={DropDownSelect}
                              defaultSelected={values.country}
                              name="country"
                              label={
                                AgentService.isAgentOrBroker()
                                  ? t('contactData.country.agentQuestion')
                                  : t('contactData.country.label')
                              }
                              options={this.countryOptions}
                              required
                            />
                          </MatchMedia>
                          <MatchMedia tablet>
                            <Field
                              component={OptionSelectorField}
                              name="country"
                              optionList={this.countryOptions}
                              required
                            />
                          </MatchMedia>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                  <Row>
                    <Col mobile={12} tablet={6} desktop={6}>
                      <Row marginBottom="s">
                        <Col>
                          <Field
                            component={Input}
                            name="postalAddress"
                            text={
                              AgentService.isAgentOrBroker()
                                ? t('contactData.postalAddress.agentQuestion')
                                : t('contactData.postalAddress.question')
                            }
                            label={t('contactData.postalAddress.label')}
                            required
                          />
                        </Col>
                      </Row>
                    </Col>
                    <Col mobile={12} tablet={6} desktop={6}>
                      <Row marginBottom="s">
                        <Col>
                          <Field
                            component={Input}
                            name="postalAddressOpt"
                            text={
                              AgentService.isAgentOrBroker()
                                ? t('contactData.postalAddress.agentQuestion')
                                : t('contactData.postalAddress.question')
                            }
                            label={t('contactData.postalAddressOpt.label')}
                          />
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                  <Row>
                    <Col mobile={12} tablet={6} desktop={6}>
                      <Row marginBottom="s">
                        <Col>
                          <Field
                            component={Input}
                            name="postalCode"
                            text={
                              AgentService.isAgentOrBroker()
                                ? t('contactData.postalCode.agentQuestion')
                                : t('contactData.postalCode.question')
                            }
                            label={t('contactData.postalCode.label')}
                            required
                          />
                        </Col>
                      </Row>
                    </Col>
                    <Col mobile={12} tablet={6} desktop={6}>
                      <Row marginBottom="s">
                        <Col>
                          <Field
                            component={Input}
                            name="city"
                            text={
                              AgentService.isAgentOrBroker()
                                ? t('contactData.city.agentQuestion')
                                : t('contactData.city.question')
                            }
                            label={t('contactData.city.label')}
                            required
                          />
                        </Col>
                      </Row>
                    </Col>
                  </Row>

                  {!AgentService.isAgentOrBroker() && (
                    <>
                      <Row marginTop="xxs" marginBottom="xxxs">
                        <Text as="p" color="black" size="m">
                          {t('contactData.privacyPolicy.header')}
                        </Text>
                      </Row>
                      <Row>
                        <Col>
                          <Field
                            component={CheckboxField}
                            name="acceptedPrivacyPolicy"
                            value={values.acceptedPrivacyPolicy}
                            defaultChecked={values.acceptedPrivacyPolicy}
                            label={
                              <Text as="div" size="s">
                                {t('contactData.privacyPolicy.description')}
                                <LinkText
                                  onClick={goToPrivacyPolicyLink}
                                  text={t('contactData.privacyPolicy.link')}
                                  color="primary"
                                />
                              </Text>
                            }
                            onSelection={() => {
                              setFieldValue(
                                'acceptedPrivacyPolicy',
                                !values.acceptedPrivacyPolicy,
                              )
                              this.setState({
                                acceptedPrivacyPolicy: !this.state
                                  .acceptedPrivacyPolicy,
                              })
                            }}
                          />
                        </Col>
                      </Row>
                    </>
                  )}

                  <Row verticalGutter marginTop="xs" marginBottom="m">
                    <Col verticalGutter mobile={12} tablet={3} desktop={3}>
                      <Button
                        primary
                        type="submit"
                        disabled={
                          !AgentService.isAgentOrBroker() &&
                          !Boolean(this.state.acceptedPrivacyPolicy)
                        }
                      >
                        {t('buttons.continue')}
                      </Button>
                    </Col>
                    <Col verticalGutter mobile={12} tablet={3} desktop={3}>
                      <Button grey onClick={this.handleClickBack}>
                        {t('buttons.back')}
                      </Button>
                    </Col>
                  </Row>
                  <Row verticalGutter>
                    <Col>
                      <ContactBox />
                    </Col>
                  </Row>
                </Form>
              )}
            />
          </Col>
        </Row>
      </>
    )
  }
}

export const PolicyholderDataView = withNamespaces()(
  withRouter(PolicyholderDataSection),
)
